import { useMemo } from 'react';

import { DateTime } from 'luxon';

import {
  AvailabilityFragment,
  MultidayAvailabilityFragment,
} from '@graphql/platform';

import {
  FROM_ISO_OPTIONS,
  hashDateTime,
} from '@shared/calendar/calendar_utils';

/**
 * Challenger-only day filter for appointment windows.
 * Compares calendar days (not absolute millis) so offset differences between
 * API windows and local MockDate still match.
 */
export const useChallengerFilteredAvailabilities = (
  availabilities: ReadonlyArray<
    AvailabilityFragment | MultidayAvailabilityFragment
  >,
  datePreferred?: DateTime,
) => {
  return useMemo(() => {
    if (!datePreferred) return [];

    return availabilities
      .filter((availability) => {
        const availabilityDate = DateTime.fromISO(
          availability.fromTime,
          FROM_ISO_OPTIONS,
        );
        if (!availabilityDate.isValid) return false;
        const sameDay =
          hashDateTime(availabilityDate) === hashDateTime(datePreferred);
        const availabilityLocal = availabilityDate.toLocal();
        const notPast =
          availabilityLocal.startOf('day') >= DateTime.now().startOf('day');
        return sameDay && notPast;
      })
      .sort(
        (a, b) =>
          DateTime.fromISO(a.fromTime, FROM_ISO_OPTIONS).toMillis() -
          DateTime.fromISO(b.fromTime, FROM_ISO_OPTIONS).toMillis(),
      );
  }, [availabilities, datePreferred]);
};
